import React, { useEffect, useState } from 'react';
import { X, ArrowRight, Calendar, FileText, MapPin } from 'lucide-react';
import { trasladoMiembroService } from '../services/trasladoMiembroService';
import { HistorialTrasladoMiembroResuelto } from '../lib/supabase';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

interface HistorialTrasladosModalProps {
  isOpen: boolean;
  onClose: () => void;
  miembroId: string;
  miembroNombre: string;
}

export const HistorialTrasladosModal: React.FC<HistorialTrasladosModalProps> = ({
  isOpen,
  onClose,
  miembroId,
  miembroNombre
}) => {
  const [traslados, setTraslados] = useState<HistorialTrasladoMiembroResuelto[]>([]);
  const [loading, setLoading] = useState(false);
  // Sin esto, un fallo de carga deja traslados=[] y se ve idéntico a "sin traslados
  // reales" — justo la lectura que el toast de abajo contradice.
  const [loadFailed, setLoadFailed] = useState(false);

  const loadHistorial = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const data = await trasladoMiembroService.obtenerHistorialPorMiembro(miembroId);
      setTraslados(data);
    } catch (e: unknown) {
      setLoadFailed(true);
      reportarErrorInesperado(e, 'No pudimos cargar el historial de traslados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && miembroId) {
      loadHistorial();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, miembroId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      level="nested"
      header={
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <ArrowRight className="w-6 h-6" />
              Historial de Traslados
            </h2>
            <p className="text-blue-100 text-sm mt-1">{miembroNombre}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-600 rounded-lg transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
      {!loading && loadFailed && (
        <div className="py-8">
          <FormErrorAlert
            error="No pudimos cargar el historial de traslados."
            action={{ label: 'Reintentar', onClick: () => { void loadHistorial(); } }}
          />
        </div>
      )}
      {!loading && !loadFailed && traslados.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Sin traslados registrados</p>
          <p className="text-slate-400 text-sm mt-2">Este miembro no tiene historial de traslados entre lumisiales</p>
        </div>
      )}
      {!loading && !loadFailed && traslados.length > 0 && (
        <div className="space-y-6">
          {traslados.map((traslado, index) => (
            <div
              key={traslado.id}
              className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-700">
                    {formatDate(traslado.fecha_traslado)}
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  Traslado #{traslados.length - index}
                </span>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Origen</p>
                    <div className="space-y-1.5">
                      <div className="text-sm">
                        <span className="text-slate-600">Provincia:</span>
                        <span className="ml-2 font-medium text-slate-800">
                          {traslado.provincia_origen_nombre}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-600">Diócesis:</span>
                        <span className="ml-2 font-medium text-slate-800">
                          {traslado.diocesis_origen_nombre}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-600">Lumisial:</span>
                        <span className="ml-2 font-semibold text-blue-700">
                          {traslado.lumisial_origen_nombre}
                        </span>
                      </div>
                      {traslado.lumisial_origen_ciudad && (
                        <div className="text-xs text-slate-500">{traslado.lumisial_origen_ciudad}</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-center">
                    <div className="bg-blue-100 rounded-full p-3">
                      <ArrowRight className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Destino</p>
                    <div className="space-y-1.5">
                      <div className="text-sm">
                        <span className="text-slate-600">Provincia:</span>
                        <span className="ml-2 font-medium text-slate-800">
                          {traslado.provincia_destino_nombre}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-600">Diócesis:</span>
                        <span className="ml-2 font-medium text-slate-800">
                          {traslado.diocesis_destino_nombre}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-slate-600">Lumisial:</span>
                        <span className="ml-2 font-semibold text-green-700">
                          {traslado.lumisial_destino_nombre}
                        </span>
                      </div>
                      {traslado.lumisial_destino_ciudad && (
                        <div className="text-xs text-slate-500">{traslado.lumisial_destino_ciudad}</div>
                      )}
                    </div>
                  </div>
                </div>

                {traslado.motivo && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Motivo</p>
                        <p className="text-sm text-slate-700">{traslado.motivo}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="-mx-6 -mb-6 mt-6 sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex justify-between items-center">
        <p className="text-sm text-slate-600">
          {traslados.length > 0 ? (
            <>Total: <span className="font-semibold">{traslados.length}</span> traslado{traslados.length !== 1 && 's'}</>
          ) : (
            'Sin traslados'
          )}
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium"
        >
          Cerrar
        </button>
      </div>
    </DialogShell>
  );
};
